import { Avatar, Button, Col, ModalV2, Row, Text } from 'components';
import { Api, theme } from 'lib';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { TableComponents } from 'screens/Admin/components';
import CreateRewardGiftModal from './CreateRewardGiftModal';

const RewardGiftsModal = ({ team, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [selectedGift, setSelectedGift] = useState(null);

  useEffect(() => {
    if (team?._id) {
      fetchRewardGifts();
    }
  }, [team]);

  const fetchRewardGifts = async (isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const offset = isLoadMore ? data.length : 0;
      const res = await Api.get(
        `/admin/rewardGifts/list?teamId=${team._id}&limit=20&offset=${offset}`
      );
      if (res?.data) {
        const newData = res.data;
        const totalCount = res.pagination?.total || 0;

        if (isLoadMore) {
          setData((prev) => {
            const existingIds = new Set(prev.map((item) => item._id));
            const uniqueNewData = newData.filter((item) => !existingIds.has(item._id));
            return [...prev, ...uniqueNewData];
          });
        } else {
          setData(newData);
        }
        setTotal(totalCount);
        setHasMore(data.length + newData.length < totalCount);
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
    setLoadingMore(false);
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 50 && hasMore && !loading && !loadingMore) {
      fetchRewardGifts(true);
    }
  };

  const handleCreateSuccess = () => {
    fetchRewardGifts();
  };

  const handleEdit = (gift) => {
    setSelectedGift(gift);
    setCreateModalVisible(true);
  };

  const handleDelete = async (gift) => {
    if (window.confirm('Are you sure you want to delete this gift?')) {
      try {
        const isSuccess = await Api.delete(`/admin/rewardGifts/delete/${gift._id}`);
        if (isSuccess) {
          fetchRewardGifts();
        }
      } catch (e) {
        console.error(e);
        alert('Failed to delete reward gift');
      }
    }
  };

  return (
    <ModalV2
      visible={true}
      closeModal={onClose}
      width="1200px"
      header={
        <Row between center wid="100%">
          <Text h3>Reward Gifts</Text>
          <Row center>
            <Row center marg="0 24px 0 0">
              <Text h4 regular>
                Total gifts:{' '}
              </Text>
              <Text h4 med marg="0 0 0 5px">
                {total}
              </Text>
            </Row>
            <Button
              text="Create Gift"
              onClick={() => {
                setSelectedGift(null);
                setCreateModalVisible(true);
              }}
              icon="add"
              bordered
              style={{ width: 'fit-content' }}
            />
          </Row>
        </Row>
      }
      hideCloseButton
      overflowX="auto"
      overflowY="hidden"
    >
      <Col pad="unset">
        <div style={{ height: '60vh', overflowY: 'auto' }} onScroll={handleScroll}>
          <TableComponents.List
            loading={loading}
            Header={() => (
              <React.Fragment>
                <TableComponents.Th first>
                  <Text>Name</Text>
                </TableComponents.Th>
                <TableComponents.Th>
                  <Text>Cost</Text>
                </TableComponents.Th>
                <TableComponents.Th>
                  <Text>Redeemed</Text>
                </TableComponents.Th>
                <TableComponents.Th>
                  <Text>Tier</Text>
                </TableComponents.Th>
                <TableComponents.Th>
                  <Text>Limits (Total/User)</Text>
                </TableComponents.Th>
                <TableComponents.Th>
                  <Text>Status</Text>
                </TableComponents.Th>
                <TableComponents.Th>
                  <Text>Created</Text>
                </TableComponents.Th>
                <TableComponents.Th last>
                  <Text>Action</Text>
                </TableComponents.Th>
              </React.Fragment>
            )}
            Items={() =>
              data.map((item, i) => (
                <TableComponents.Tr key={item._id || i}>
                  <TableComponents.Td first>
                    <Row center>
                      {item.image && (
                        <img
                          src={item.image}
                          alt={item.name}
                          style={{
                            width: 60,
                            height: 60,
                            borderRadius: 8,
                            marginRight: 12,
                            objectFit: 'cover'
                          }}
                        />
                      )}
                      <Text med>{item.name}</Text>
                    </Row>
                  </TableComponents.Td>
                  <TableComponents.Td>
                    <Text>{item.pointCost}</Text>
                  </TableComponents.Td>
                  <TableComponents.Td>
                    <Text>{item.redeemedCount}</Text>
                  </TableComponents.Td>
                  <TableComponents.Td>
                    <Text style={{ textTransform: 'capitalize' }}>{item.tier}</Text>
                  </TableComponents.Td>
                  <TableComponents.Td>
                    <Text>
                      {item.limitTotal || '∞'} / {item.limitPerUser || '∞'}
                    </Text>
                  </TableComponents.Td>
                  <TableComponents.Td>
                    <Text col={item.isActive ? theme.GREEN : theme.GREY_60}>
                      {item.isActive ? 'Active' : 'Inactive'}
                    </Text>
                  </TableComponents.Td>
                  <TableComponents.Td>
                    <Text>
                      {item.createdAt ? format(new Date(item.createdAt), 'MMM dd, yyyy') : '-'}
                    </Text>
                  </TableComponents.Td>
                  <TableComponents.Td last>
                    <Row>
                      <Button
                        text="Edit"
                        onClick={() => handleEdit(item)}
                        bg={theme.WHITE}
                        col={theme.PRIMARY}
                        bordered
                        style={{
                          padding: '4px 12px',
                          height: 'auto',
                          fontSize: 13,
                          marginRight: 8
                        }}
                      />
                      <Button
                        text="Delete"
                        onClick={() => handleDelete(item)}
                        bg={theme.WHITE}
                        col={theme.RED}
                        bordered
                        style={{ padding: '4px 12px', height: 'auto', fontSize: 13 }}
                      />
                    </Row>
                  </TableComponents.Td>
                </TableComponents.Tr>
              ))
            }
          />
          {loadingMore && (
            <Row centerAll marg="10px 0">
              <Text>Loading more...</Text>
            </Row>
          )}
        </div>
      </Col>
      {createModalVisible && (
        <CreateRewardGiftModal
          team={team}
          giftToEdit={selectedGift}
          onClose={() => {
            setCreateModalVisible(false);
            setSelectedGift(null);
          }}
          onSuccess={handleCreateSuccess}
        />
      )}
    </ModalV2>
  );
};

export default RewardGiftsModal;
